import type { Round, Trip } from "../types";
import { getEffectivePH, formatPlayingHandicap, shotsOnHole, holeInPlayingRange, isMultiStartRound, revealHoleForRound, COUNTBACK_SLICES, type CountbackTuple, compareCountback, compareCountbackNSP } from "../golf";
import { effectiveCourseForPlayer } from "./effectiveCourse";
import { groupDisplayName } from "./groupOrder";

/**
 * Compute the Texas Scramble team Playing Handicap for a given team.
 *
 * Steps:
 *  1. Resolve each player's individual Playing Handicap (course-adjusted integer).
 *  2. Sort PHs low -> high (lowest handicapper first).
 *  3. Multiply each PH by its positional allowance percentage.
 *  4. Either round the sum ("total") or round each share then sum ("each").
 */
export function computeTeamPH(
  round: Round,
  teamIndex: number,
  trip: Trip,
): number {
  const teamPids = round.teams?.[teamIndex] ?? [];
  const players = teamPids
    .map((pid) => trip.players?.find((p) => p.id === pid))
    .filter(Boolean) as NonNullable<typeof trip.players>[number][];
  if (players.length === 0) return 0;

  const size = players.length;
  const key = size === 2 ? "pairs" : size === 3 ? "trios" : "quads";
  const allowances: number[] | undefined =
    round.scramble_config?.allowances?.[key];
  if (!allowances || allowances.length !== size) return 0;

  // Playing Handicaps (integers, already course-adjusted)
  const phs = players.map((p) =>
    getEffectivePH(p, effectiveCourseForPlayer(round, p.id)),
  );

  // Sort low -> high so allowance[0] applies to lowest handicap
  const sortedPhs = phs.slice().sort((a, b) => a - b);

  // Integer math: ph * pct
  const products = sortedPhs.map((ph, i) => ph * allowances[i]);

  const rounding = round.scramble_config?.rounding ?? "total";
  if (rounding === "each") {
    return products.reduce((sum, p) => sum + Math.round(p / 100), 0);
  }
  // "total" (default)
  return Math.round(products.reduce((s, p) => s + p, 0) / 100);
}

/** Default min_drives for a given team size: floor(18 / team_size). */
export function defaultMinDrives(teamSize: number): number {
  return Math.floor(18 / teamSize);
}

/** Default scramble_config object for a given team size. */
export function defaultScrambleConfig(teamSize: number) {
  return {
    allowances: {
      pairs: [35, 15],
      trios: [30, 20, 10],
      quads: [25, 20, 15, 10],
    },
    rounding: "total" as const,
    min_drives: defaultMinDrives(teamSize),
  };
}

/**
 * Balanced team draw for Texas Scramble rounds.
 * 
 * Minimises max(teamPH) - min(teamPH) across all teams using:
 * 1. Snake draft (sorted by PH descending) as a starting point
 * 2. Iterative swap improvement until no pair-swap reduces the spread
 * 
 * Uses computeTeamPH internally — same formula as setup preview and scoring.
 * Deterministic for identical inputs (tie-breaks by player ID).
 */
export function balancedScrambleDraw(
  round: Round,
  trip: Trip,
  playerIds: string[],
  teamSize: number,
): string[][] {
  const n = playerIds.length;
  if (n === 0) return [];
  const numTeams = Math.ceil(n / teamSize);
  if (numTeams <= 1) return [playerIds.slice()];

  const players = trip.players ?? [];
  const phMap: Record<string, number> = {};
  for (const pid of playerIds) {
    const p = players.find((pl) => pl.id === pid);
    phMap[pid] = p ? getEffectivePH(p, effectiveCourseForPlayer(round, p.id)) : 0;
  }

  // Sort by PH descending, then by ID for deterministic tie-breaks
  const sorted = playerIds.slice().sort((a, b) => {
    const diff = phMap[b] - phMap[a];
    return diff !== 0 ? diff : a.localeCompare(b);
  });

  // Snake draft into numTeams teams
  const teams: string[][] = Array.from({ length: numTeams }, () => []);
  let forward = true;
  let idx = 0;
  for (const pid of sorted) {
    teams[idx].push(pid);
    if (forward) {
      if (idx >= numTeams - 1) forward = false;
      else idx++;
    } else {
      if (idx <= 0) forward = true;
      else idx--;
    }
  }

  // Helper: compute team PH using the real shared function
  const tph = (pids: string[]): number => {
    if (pids.length === 0) return 0;
    const fakeRound = { ...round, teams: [pids] } as Round;
    return computeTeamPH(fakeRound, 0, trip);
  };

  const getSpread = (): number => {
    const phs = teams.map((t) => tph(t));
    return Math.max(...phs) - Math.min(...phs);
  };

  // Swap improvement: iterate until no single pair-swap reduces spread
  let improved = true;
  while (improved) {
    improved = false;
    const currentSpread = getSpread();
    if (currentSpread <= 0) break;
    for (let i = 0; i < numTeams && !improved; i++) {
      for (let j = i + 1; j < numTeams && !improved; j++) {
        for (let pi = 0; pi < teams[i].length && !improved; pi++) {
          for (let pj = 0; pj < teams[j].length && !improved; pj++) {
            // Swap
            const tmp = teams[i][pi];
            teams[i][pi] = teams[j][pj];
            teams[j][pj] = tmp;
            if (getSpread() < currentSpread) {
              improved = true; // Restart outer loop
            } else {
              // Undo
              teams[j][pj] = teams[i][pi];
              teams[i][pi] = tmp;
            }
          }
        }
      }
    }
  }

  return teams;
}



/**
 * Compute the Net Double Bogey gross for a team NR on a specific hole.
 * NDB = par + 2 + shotsOnHole(teamPH, SI)
 */
export function computeTeamNDB(
  round: Round,
  teamIndex: number,
  trip: Trip,
  holeIdx: number,
): number {
  const par = round.hole_pars?.[holeIdx] ?? 4;
  const si = round.stroke_indexes?.[holeIdx] ?? 1;
  const teamPH = computeTeamPH(round, teamIndex, trip);
  return par + 2 + shotsOnHole(teamPH, si);
}

/**
 * Drive-count tracking for a team.
 * Returns a map of player_id → number of drives used so far.
 */
export function driveCountsForTeam(
  round: Round,
  teamIndex: number,
): Record<string, number> {
  const owners = round.team_drive_owner?.[String(teamIndex)] ?? [];
  const counts: Record<string, number> = {};
  for (const pid of owners) {
    if (pid) counts[pid] = (counts[pid] ?? 0) + 1;
  }
  return counts;
}

/**
 * Compute the number of holes scored so far for a team.
 */
export function teamHolesScored(round: Round, teamIndex: number): number {
  const strokes = round.team_hole_strokes?.[String(teamIndex)] ?? [];
  return strokes.filter((v) => v > 0).length;
}

export type DriveWarning = {
  playerId: string;
  drivesUsed: number;
  drivesNeeded: number;
  holesRemaining: number;
  severity: "soft" | "hard";
};

/**
 * Compute drive warnings for each player on a team.
 * Soft = drivesNeeded > 0 && drivesNeeded === holesRemaining
 * Hard = drivesNeeded > holesRemaining
 */
export function driveWarnings(
  round: Round,
  teamIndex: number,
): DriveWarning[] {
  const minDrives = round.scramble_config?.min_drives ?? 0;
  if (minDrives <= 0) return [];
  const counts = driveCountsForTeam(round, teamIndex);
  const scored = teamHolesScored(round, teamIndex);
  const holesRemaining = 18 - scored;
  const teamPids = round.teams?.[teamIndex] ?? [];
  const warnings: DriveWarning[] = [];
  for (const pid of teamPids) {
    const used = counts[pid] ?? 0;
    const needed = minDrives - used;
    if (needed > 0 && needed >= holesRemaining) {
      warnings.push({
        playerId: pid,
        drivesUsed: used,
        drivesNeeded: needed,
        holesRemaining,
        severity: needed > holesRemaining ? "hard" : "soft",
      });
    }
  }
  return warnings;
}

/**
 * Get the missing (unscored, non-NR) holes for a scramble team.
 * Returns an array of 0-based hole indexes.
 */
export function scrambleMissingHoles(
  round: Round,
  teamIndex: number,
): number[] {
  const tk = String(teamIndex);
  const strokes = round.team_hole_strokes?.[tk] ?? [];
  const nrHoles = round.team_hole_nr?.[tk] ?? [];
  const missing: number[] = [];
  for (let h = 0; h < 18; h++) {
    if ((strokes[h] ?? 0) <= 0 && !nrHoles.includes(h)) {
      missing.push(h);
    }
  }
  return missing;
}


// ── Scramble Leaderboard ──

export type ScrambleStanding = {
  team_index: number;
  team_name: string;
  team_ph: number;
  total: number;
  gross: number;
  thru: number;
  par_played: number;
  countback_tuple: CountbackTuple;
  drives_under_min: boolean;
  under_min_players: string[];
  rank: number;
  cb: boolean;
};

/** Sum team_hole_scores for a scramble team over a set of hole indexes. */
function scrambleHoleSum(round: Round, teamIdx: number, holes: number[]): number {
  const scores = round.team_hole_scores?.[String(teamIdx)] ?? [];
  let total = 0;
  for (const h of holes) {
    const v = scores[h];
    if (v != null && v >= 0) total += v;
  }
  return total;
}

/** Build a Stableford countback tuple for a scramble team. */
function scrambleCountbackStableford(round: Round, teamIdx: number): CountbackTuple {
  return COUNTBACK_SLICES.map((slice) =>
    scrambleHoleSum(round, teamIdx, slice),
  ) as CountbackTuple;
}

/** Sum net strokes for a scramble team over a set of hole indexes. */
function scrambleNetSum(
  round: Round,
  teamIdx: number,
  teamPH: number,
  holes: number[],
): number {
  const strokes = round.team_hole_strokes?.[String(teamIdx)] ?? [];
  const si = round.stroke_indexes ?? [];
  let total = 0;
  for (const h of holes) {
    const s = strokes[h] ?? 0;
    if (s <= 0) continue; // unscored
    total += s - shotsOnHole(teamPH, si[h] ?? 1);
  }
  return total;
}

/** Build an NSP countback tuple for a scramble team. */
function scrambleCountbackNSP(
  round: Round,
  teamIdx: number,
  teamPH: number,
): CountbackTuple {
  return COUNTBACK_SLICES.map((slice) =>
    scrambleNetSum(round, teamIdx, teamPH, slice),
  ) as CountbackTuple;
}

/**
 * Compute the scramble team leaderboard.
 *
 * @param throughPos  1-indexed playing-position cap. If omitted AND freeze is
 *                    active (freeze_enabled && !revealed), auto-applies
 *                    reveal_hole. Pass `undefined` + `revealed:true` for full 18.
 */
export function computeScrambleLeaderboard(
  round: Round,
  trip: Trip,
  throughPos?: number,
): ScrambleStanding[] {
  const teams = round.teams ?? [];
  if (teams.length === 0) return [];

  const isNSP = (round.format ?? "stableford") === "nsp";
  const minDrives = round.scramble_config?.min_drives ?? 0;

  // Auto-apply freeze
  let effectiveThroughPos = throughPos;
  if (effectiveThroughPos == null) {
    if (round.freeze_enabled !== false && !round.revealed) {
      effectiveThroughPos = revealHoleForRound(round); // 1-indexed
    }
  }

  const standings: ScrambleStanding[] = [];

  for (let t = 0; t < teams.length; t++) {
    const tk = String(t);
    const tph = computeTeamPH(round, t, trip);
    const strokes = round.team_hole_strokes?.[tk] ?? [];
    const scores = round.team_hole_scores?.[tk] ?? [];

    let total = 0;
    let gross = 0;
    let thru = 0;
    let par_played = 0;
    const hp = round.hole_pars ?? [];

    if (isNSP) {
      // NSP: sum net for scored holes within range (partial allocation)
      const si = round.stroke_indexes ?? [];
      for (let h = 0; h < 18; h++) {
        if (effectiveThroughPos != null) {
          if (isMultiStartRound(round)) {
            if (!holeInPlayingRange(round, t, h, effectiveThroughPos)) continue;
          } else {
            if (h >= effectiveThroughPos) continue;
          }
        }
        const s = strokes[h] ?? 0;
        if (s > 0) {
          gross += s;
          total += s - shotsOnHole(tph, si[h] ?? 1);
          par_played += hp[h] ?? 0;
          thru++;
        }
      }
    } else {
      // Stableford: sum points for holes within range
      for (let h = 0; h < 18; h++) {
        if (effectiveThroughPos != null) {
          if (isMultiStartRound(round)) {
            if (!holeInPlayingRange(round, t, h, effectiveThroughPos)) continue;
          } else {
            if (h >= effectiveThroughPos) continue;
          }
        }
        const s = strokes[h] ?? 0;
        if (s > 0) {
          total += Math.max(0, scores[h] ?? 0);
          par_played += hp[h] ?? 0;
          thru++;
        }
      }
    }

    // Countback
    const cb = isNSP
      ? scrambleCountbackNSP(round, t, tph)
      : scrambleCountbackStableford(round, t);

    // Drives under min — only flag when team has finished the round
    let drivesUnderMin = false;
    const underMinPlayers: string[] = [];
    if (minDrives > 0) {
      const teamStrokes = round.team_hole_strokes?.[tk] ?? [];
      const teamNr = round.team_hole_nr?.[tk] ?? [];
      const gf = (round as any).group_finished?.[t] === true;
      const allHolesDone = (() => { for (let h = 0; h < 18; h++) { if ((teamStrokes[h] ?? 0) <= 0 && !teamNr.includes(h)) return false; } return true; })();
      const isFinished = gf || allHolesDone;
      if (isFinished) {
        const counts = driveCountsForTeam(round, t);
        for (const pid of teams[t]) {
          if ((counts[pid] ?? 0) < minDrives) {
            drivesUnderMin = true;
            underMinPlayers.push(pid);
          }
        }
      }
    }

    standings.push({
      team_index: t,
      team_name: groupDisplayName(round, t),
      team_ph: tph,
      total,
      gross,
      thru,
      par_played,
      countback_tuple: cb,
      drives_under_min: drivesUnderMin,
      under_min_players: underMinPlayers,
      rank: 0,
      cb: false,
    });
  }

  // Sort
  if (isNSP) {
    standings.sort((a, b) => {
      const aUnplayed = a.par_played === 0;
      const bUnplayed = b.par_played === 0;
      if (aUnplayed && bUnplayed) return 0;
      if (aUnplayed) return 1;
      if (bUnplayed) return -1;
      const aTP = a.total - a.par_played;
      const bTP = b.total - b.par_played;
      if (aTP !== bTP) return aTP - bTP; // lower TO PAR wins
      return compareCountbackNSP(a.countback_tuple, b.countback_tuple);
    });
  } else {
    standings.sort((a, b) => {
      if (a.total !== b.total) return b.total - a.total; // higher is better
      return compareCountback(a.countback_tuple, b.countback_tuple);
    });
  }

  // Assign ranks (use TO PAR for NSP, total for Stableford)
  for (let i = 0; i < standings.length; i++) {
    if (i === 0) {
      standings[i].rank = 1;
    } else {
      // Unplayed teams never tie with played teams
      const curUnplayed = standings[i].par_played === 0;
      const prevUnplayed = standings[i - 1].par_played === 0;
      const sameScore = !curUnplayed && !prevUnplayed && (isNSP
        ? (standings[i].total - standings[i].par_played) === (standings[i - 1].total - standings[i - 1].par_played)
        : standings[i].total === standings[i - 1].total);
      if (sameScore) {
        // Check countback
        const cmp = isNSP
          ? compareCountbackNSP(standings[i].countback_tuple, standings[i - 1].countback_tuple)
          : compareCountback(standings[i].countback_tuple, standings[i - 1].countback_tuple);
        if (cmp === 0) {
          standings[i].rank = standings[i - 1].rank;
          standings[i].cb = false;
        } else {
          standings[i].rank = i + 1;
          standings[i].cb = true;
          if (standings[i - 1].rank === standings[i].rank - 1 ||
              (i >= 2 && (isNSP
                ? (standings[i - 1].total - standings[i - 1].par_played) === (standings[i - 2].total - standings[i - 2].par_played)
                : standings[i - 1].total === standings[i - 2].total))) {
            standings[i - 1].cb = true;
          }
        }
      } else {
        standings[i].rank = i + 1;
      }
    }
  }

  return standings;
}
